import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Edit, Shield, AlertTriangle } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';

function ClientPersonalInfo({ db, client }) {
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModificationForm, setShowModificationForm] = useState(false);
  const [modificationRequest, setModificationRequest] = useState({
    field: '',
    currentValue: '',
    requestedValue: '',
    justification: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const { theme } = useTheme();

  useEffect(() => {
    loadClientData();
  }, [client]);

  const loadClientData = async () => {
    try {
      const clientDoc = await getDoc(doc(db, 'clients', client.id));
      if (clientDoc.exists()) {
        setClientData({
          id: clientDoc.id,
          ...clientDoc.data()
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données client:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  };

  const handleModificationRequest = async (e) => {
    e.preventDefault();
    
    try {
      // Créer une demande de modification
      const requestData = {
        clientId: client.id,
        clientName: `${clientData.firstName} ${clientData.lastName}`,
        field: modificationRequest.field,
        currentValue: modificationRequest.currentValue,
        requestedValue: modificationRequest.requestedValue,
        justification: modificationRequest.justification,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'rgpd_modification_requests'), requestData);
      
      setMessage({ 
        type: 'success', 
        text: 'Votre demande de modification a été envoyée. Elle sera traitée par notre équipe.' 
      });
      
      setShowModificationForm(false);
      setModificationRequest({
        field: '',
        currentValue: '',
        requestedValue: '',
        justification: ''
      });
    } catch (error) {
      console.error('Erreur lors de la demande de modification:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'envoi de la demande' });
    }
  };

  const formatAddress = (address) => {
    if (!address || typeof address !== 'object') return 'Non renseignée';
    
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.postalCode) parts.push(address.postalCode);
    if (address.city) parts.push(address.city);
    if (address.country) parts.push(address.country);
    
    return parts.length > 0 ? parts.join(', ') : 'Non renseignée';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de vos informations...</p>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">Impossible de charger vos informations personnelles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="text-blue-600 dark:text-blue-400 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mes informations personnelles</h2>
              <p className="text-gray-600 dark:text-gray-400">Conformément au RGPD, vous avez le droit de consulter et de rectifier vos données.</p>
            </div>
          </div>
          <button
            onClick={() => setShowModificationForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit size={16} className="mr-2" />
            Demander une modification
          </button>
        </div>
      </div>

      {/* Message d'état */}
      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border-green-200' 
            : 'bg-red-50 text-red-800 border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Informations personnelles */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
        <div className="p-6 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <User className="mr-2 text-blue-600 dark:text-blue-400" size={20} />
            Identité
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                {clientData.firstName || 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                {clientData.lastName || 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 flex items-center text-gray-900 dark:text-white">
                <Calendar className="mr-2 text-gray-400 dark:text-gray-500" size={16} />
                {clientData.birthDate ? (
                  clientData.birthDate.toDate
                    ? clientData.birthDate.toDate().toLocaleDateString('fr-FR')
                    : new Date(clientData.birthDate).toLocaleDateString('fr-FR')
                ) : 'Non renseignée'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type d'entité</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                {clientData.typeEntite || 'Non renseigné'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coordonnées */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
        <div className="p-6 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Mail className="mr-2 text-blue-600 dark:text-blue-400" size={20} />
            Coordonnées
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 flex items-center text-gray-900 dark:text-white">
                <Mail className="mr-2 text-gray-400 dark:text-gray-500" size={16} />
                {clientData.email || 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 flex items-center text-gray-900 dark:text-white">
                <Phone className="mr-2 text-gray-400 dark:text-gray-500" size={16} />
                {clientData.phoneNumber || 'Non renseigné'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Adresse */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
        <div className="p-6 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <MapPin className="mr-2 text-blue-600 dark:text-blue-400" size={20} />
            Adresse
          </h3>
        </div>
        
        <div className="p-6">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
            {formatAddress(clientData.address)}
          </div>
          {clientData.additionalInfo && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Informations complémentaires</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-600 text-gray-900 dark:text-white">
                {clientData.additionalInfo}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de demande de modification */}
      {showModificationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Demander une modification</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                Votre demande sera examinée par notre équipe sous 48h.
              </p>
            </div>
            
            <form onSubmit={handleModificationRequest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Champ à modifier
                </label>
                <select
                  value={modificationRequest.field}
                  onChange={(e) => setModificationRequest(prev => ({ ...prev, field: e.target.value }))}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Sélectionner un champ</option>
                  <option value="firstName">Prénom</option>
                  <option value="lastName">Nom</option>
                  <option value="email">Email</option>
                  <option value="phoneNumber">Téléphone</option>
                  <option value="birthDate">Date de naissance</option>
                  <option value="address">Adresse</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nouvelle valeur
                </label>
                <input
                  type="text"
                  value={modificationRequest.requestedValue}
                  onChange={(e) => setModificationRequest(prev => ({ ...prev, requestedValue: e.target.value }))}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Nouvelle valeur souhaitée"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Justification
                </label>
                <textarea
                  value={modificationRequest.justification}
                  onChange={(e) => setModificationRequest(prev => ({ ...prev, justification: e.target.value }))}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="3"
                  placeholder="Pourquoi souhaitez-vous modifier cette information ?"
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModificationForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Envoyer la demande
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientPersonalInfo;
